import { useState } from 'react';
import {
  Palette, Image as ImageIcon, Car, CarFront, Wrench, Calendar, Phone,
  Share2, MapPin, MessageCircle, Type, KeyRound, RotateCcw, ChevronDown, ChevronUp,
  Plus, Trash2, Lock, Megaphone, Settings, Upload, Loader2, PanelTop,
} from 'lucide-react';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';

// car-builder-100-funcional, Fase 5 (restyle): secciones colapsables con
// icono + descripcion, sobre el MISMO schema/comportamiento ya validado en
// LAB (sections/landing_toggles/dealer_info) -- ver CarBuilderPage.js para
// el porque no se reescribio a un schema nuevo.
//
// Refresh (dev-builder UI/UX aplicado al editor, no a la landing publica):
// inputs/textareas/botones pasan a los componentes shadcn ya usados en el
// wizard de esta misma pagina (Input/Textarea/Button), iconos emoji -> lucide
// (mismo set que components/dev-builder/*), y un selector de color para
// `accent_color` (campo YA existente en el schema/backend -- ver
// _render_html en car_builder.py, solo faltaba exponerlo en el editor).
// El checkbox de toggles se deja nativo a propósito: un <input type=checkbox>
// es lo que los tests e2e existentes saben leer/marcar; el shadcn Checkbox
// (Radix, sin `type=checkbox` real) cambiaría esa superficie sin necesidad.

const labelCls = 'block text-xs font-medium text-gray-600 mb-1';
const warnCls = 'text-xs text-amber-600 mt-1';
const counterCls = 'text-[11px] text-gray-400 tabular-nums';

function FieldRow({ label, counter, children }) {
  return (
    <div className="mb-2">
      {(label || counter != null) && (
        <div className="flex items-center justify-between mb-1">
          {label && <label className={labelCls}>{label}</label>}
          {counter != null && <span className={counterCls}>{counter}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

function RevertButton({ onClick, disabled, title }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (!disabled) onClick(); }}
      disabled={disabled}
      title={title || 'Restablecer a default'}
      className="shrink-0 w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:text-red-600 hover:border-red-300 disabled:opacity-40 disabled:cursor-not-allowed"
    >
      <RotateCcw className="h-4 w-4" />
    </button>
  );
}

function ColorField({ label, value, defaultValue, disabled, onChange, testid = 'car-builder-color' }) {
  const current = value || defaultValue;
  return (
    <div className="flex items-center justify-between gap-2">
      <Label className="text-xs font-medium text-gray-600">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className="w-9 h-9 rounded-md border border-gray-300 shrink-0 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: current }}
            title={current}
            data-testid={`${testid}-swatch`}
          />
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3">
          <input
            type="color"
            value={current}
            onChange={(e) => onChange(e.target.value)}
            className="w-24 h-24 cursor-pointer border-0 p-0 bg-transparent"
            data-testid={`${testid}-input`}
          />
          <p className="text-xs text-center text-gray-500 mt-1 font-mono">{current}</p>
        </PopoverContent>
      </Popover>
    </div>
  );
}

function UploadButton({ uploading, disabled, title, onFile, testid, accept = 'image/*' }) {
  return (
    <label
      className={`shrink-0 w-9 h-9 flex items-center justify-center rounded-md border border-gray-300 text-gray-500 hover:text-blue-600 hover:border-blue-300 cursor-pointer ${disabled || uploading ? 'opacity-40 pointer-events-none' : ''}`}
      title={title || 'Subir imagen'}
    >
      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
      <input
        type="file"
        accept={accept}
        className="hidden"
        data-testid={testid}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onFile(file);
        }}
      />
    </label>
  );
}

function Section({ icon: Icon, title, description, defaultOpen = false, testid, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gray-50"
        data-testid={testid ? `${testid}-header` : undefined}
      >
        <span className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center shrink-0 text-gray-600">
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex-1 min-w-0 pt-1">
          <span className="block text-sm font-semibold text-gray-900">{title}</span>
          {description && <span className="block text-xs text-gray-500 mt-0.5">{description}</span>}
        </span>
        <span className="text-gray-400 shrink-0 pt-1.5">{open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}</span>
      </button>
      <div className={`overflow-hidden transition-[max-height] duration-300 ease-in-out ${open ? 'max-h-[1200px]' : 'max-h-0'}`}>
        <div className="px-4 pb-4 space-y-2">{children}</div>
      </div>
    </div>
  );
}

export default function SidebarEditor({
  site,
  landingToggles,
  dealerInfo,
  fontOptions,
  hasCustomHtml,
  isAdmin,
  saving,
  publishing,
  resetting,
  revertingField,
  publishStatus,
  isLikelyUrl,
  isLikelyPhone,
  onFieldChange,
  onSectionChange,
  onToggleChange,
  onDealerInfoChange,
  onPromotionsChange,
  onTypographyColorsChange,
  onFontsChange,
  onTextStylesChange,
  onWritersChange,
  onUploadImage,
  onUploadFont,
  onRevert,
  onSave,
  onPublish,
  onResetAll,
}) {
  const sections = site.sections || {};
  const disabled = !!hasCustomHtml;
  const [uploadingField, setUploadingField] = useState(null);

  const handleUpload = async (fieldKey, file) => {
    if (!onUploadImage) return;
    setUploadingField(fieldKey);
    try {
      const url = await onUploadImage(file);
      onFieldChange({ [fieldKey]: url });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la imagen');
    } finally {
      setUploadingField(null);
    }
  };

  // UI simplificada de Tipografía (2026-09-11): una sola fuente custom,
  // guardada en typography_colors.font_file_url (mismo campo que ya lee
  // _render_html en el backend para inyectar el @font-face).
  const handleTypographyFontUpload = async (file) => {
    if (!onUploadFont) return;
    setUploadingField('typography-font');
    try {
      const url = await onUploadFont(file);
      onTypographyColorsChange({ font_file_url: url });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudo subir la fuente');
    } finally {
      setUploadingField(null);
    }
  };

  // Colores por sección (fondo + texto) -- pedido explícito de Alexis
  // (2026-09-12). Fondo ya existía en el backend/render desde antes
  // (typography_colors.section_background_colors) pero sin UI desde la
  // simplificación de Tipografía del 2026-09-11; texto es NUEVO (nunca
  // existió aquí ni en dev-builder, solo aplica al título de la sección
  // -- y a la descripción, en Promociones -- ver _render_html/
  // CarSitePublicPage.js). Ambos viven en el mismo objeto typography_colors,
  // por eso reusa onTypographyColorsChange igual que la fuente custom.
  const updateSectionColor = (sectionKey, kind, value) => {
    const field = kind === 'bg' ? 'section_background_colors' : 'section_text_colors';
    const current = typographyColors[field] || {};
    onTypographyColorsChange({ [field]: { ...current, [sectionKey]: value } });
  };

  const sectionEditor = (key, defaultOpen) => {
    const s = sections[key] || { title: '', subtitle: '' };
    return (
      <>
        <FieldRow label="Título de sección" counter={`${(s.title || '').length}/80`}>
          <Input
            type="text"
            maxLength={80}
            disabled={disabled}
            value={s.title || ''}
            onChange={(e) => onSectionChange(key, { title: e.target.value })}
            data-testid={key === 'home' ? 'car-builder-section-title-input' : `car-builder-section-title-input-${key}`}
          />
        </FieldRow>
        <FieldRow label="Subtítulo / texto auxiliar" counter={`${(s.subtitle || '').length}/200`}>
          <Textarea
            maxLength={200}
            disabled={disabled}
            className="min-h-20"
            value={s.subtitle || ''}
            onChange={(e) => onSectionChange(key, { subtitle: e.target.value })}
            data-testid={key === 'home' ? 'car-builder-section-subtitle-input' : `car-builder-section-subtitle-input-${key}`}
          />
        </FieldRow>
        <p className="text-[11px] text-gray-400">El botón ↺ de esta sección restablece los títulos de TODAS las secciones a la vez (comparten un solo campo).</p>
      </>
    );
  };

  // typography_colors sigue viviendo en el backend (color de acento, fondos
  // por sección, etc. -- ver _resolve_typography_colors) como quedó de la
  // consolidación anterior, pero sin UI propia desde 2026-09-11 (pedido
  // explícito de Alexis: simplificar a SOLO la fuente custom, modelo
  // dev-builder). `typographyColors` se sigue leyendo aquí porque
  // font_file_url vive en el mismo objeto.
  const typographyColors = site.typography_colors || {};

  const promotions = site.promotions || {};
  const promoItems = Array.isArray(promotions.items) ? promotions.items : [];
  const updatePromoItems = (items) => onPromotionsChange({ items });
  const updatePromoItem = (idx, patch) => updatePromoItems(promoItems.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const addPromoItem = () => updatePromoItems([...promoItems, {
    id: `promo-${Date.now()}-${promoItems.length}`, model_name: '', image_url: '', benefit_1: '', benefit_2: '', benefit_3: '',
  }]);

  const toggleRow = (key, label) => (
    <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
      <span className="text-xs text-gray-700">{label}</span>
      <input
        type="checkbox"
        disabled={disabled}
        checked={!!landingToggles[key]}
        onChange={(e) => onToggleChange(key, e.target.checked)}
        className="w-4 h-4 accent-blue-600 disabled:opacity-40"
        data-testid={`landing-toggle-${key}`}
      />
    </label>
  );

  return (
    <aside className="w-full lg:w-80 shrink-0 bg-white border-r border-gray-200 flex flex-col h-full" data-testid="car-builder-sidebar">
      <div className="px-4 py-4 border-b border-gray-200">
        <h1 className="text-base font-bold text-gray-900" data-testid="car-builder-title">Landing Page Builder</h1>
        <p className="text-xs text-gray-500 mt-0.5" data-testid="car-builder-subtitle">Cada sección es a la vez su navegación y su editor.</p>
      </div>

      <div className="flex-1 overflow-y-auto relative" data-testid="car-builder-sections-scroll">
        {disabled && (
          <div className="absolute inset-0 z-10 bg-gray-100/85 backdrop-blur-[1px] flex items-start justify-center pt-10 px-6" data-testid="car-builder-custom-landing-overlay">
            <div className="bg-white border border-gray-300 rounded-lg shadow-sm p-4 text-center max-w-[240px]">
              <Lock className="h-6 w-6 mx-auto mb-1 text-gray-400" />
              <p className="text-sm font-semibold text-gray-900">Esta landing fue personalizada a la medida</p>
              <p className="text-xs text-gray-500 mt-1">El editor de secciones no aplica aquí — contacta a soporte si necesitas un cambio.</p>
            </div>
          </div>
        )}

        <Section icon={Palette} title="Marca" description="Nombre, dirección pública y logo" defaultOpen testid="car-builder-section-marca">
          <FieldRow label="Nombre de agencia" counter={`${(site.agency_name || '').length}/60`}>
            <div className="flex gap-1">
              <Input type="text" maxLength={60} disabled={disabled} value={site.agency_name || ''} onChange={(e) => onFieldChange({ agency_name: e.target.value })} data-testid="car-builder-agency-name-input" />
              <RevertButton disabled={disabled || revertingField === 'agency_name'} onClick={() => onRevert('agency_name')} title="Restablecer nombre a default" />
            </div>
          </FieldRow>
          <FieldRow label="Slug público">
            <Input type="text" disabled={disabled} value={site.slug || ''} onChange={(e) => onFieldChange({ slug: e.target.value })} data-testid="car-builder-slug-input" />
          </FieldRow>
          <FieldRow label="Logo URL">
            <div className="flex gap-1">
              <Input type="text" disabled={disabled} value={site.logo_url || ''} onChange={(e) => onFieldChange({ logo_url: e.target.value })} data-testid="car-builder-logo-input" />
              <RevertButton disabled={disabled || revertingField === 'logo_url'} onClick={() => onRevert('logo_url')} title="Restablecer logo a default" />
            </div>
            {!isLikelyUrl(site.logo_url) && <p className={warnCls}>Debería empezar con http:// o https://</p>}
          </FieldRow>
          <p className="text-[11px] text-gray-400">Los colores y fondos de toda la landing se editan en un solo lugar: la sección "Tipografía", más abajo.</p>
        </Section>

        <Section icon={PanelTop} title="Configuración General" description="Color del NAV y texto del footer" testid="car-builder-section-general">
          <FieldRow label="Color de fondo del NAV">
            <ColorField testid="car-builder-nav-bg-color" label="Fondo de la barra de navegación" value={site.nav_bg_color} defaultValue="#0f172a" disabled={disabled} onChange={(v) => onFieldChange({ nav_bg_color: v })} />
          </FieldRow>
          <FieldRow label="Texto del footer" counter={`${(site.footer_text || '').length}/200`}>
            <div className="flex gap-1">
              <Input type="text" maxLength={200} disabled={disabled} value={site.footer_text || ''} onChange={(e) => onFieldChange({ footer_text: e.target.value })} data-testid="car-builder-footer-text-input" />
              <RevertButton disabled={disabled || revertingField === 'footer_text'} onClick={() => onRevert('footer_text')} title="Restablecer texto del footer a default" />
            </div>
          </FieldRow>
        </Section>

        <Section icon={ImageIcon} title="Hero" description="Imagen y texto principal de portada" defaultOpen testid="car-builder-section-hero">
          <FieldRow label="Imagen hero URL">
            <div className="flex gap-1">
              <Input type="text" disabled={disabled} value={site.hero_image || ''} onChange={(e) => onFieldChange({ hero_image: e.target.value })} data-testid="car-builder-hero-image-input" />
              <UploadButton disabled={disabled} uploading={uploadingField === 'hero_image'} onFile={(file) => handleUpload('hero_image', file)} title="Subir imagen de hero" testid="car-builder-hero-image-upload" />
              <RevertButton disabled={disabled || revertingField === 'hero_image'} onClick={() => onRevert('hero_image')} title="Restablecer imagen hero a default" />
            </div>
            {!isLikelyUrl(site.hero_image) && <p className={warnCls}>Debería empezar con http:// o https://</p>}
          </FieldRow>
          {sectionEditor('home')}
          <div className="pt-1">
            <RevertButton disabled={disabled || revertingField === 'sections'} onClick={() => onRevert('sections')} title="Restablecer TODAS las secciones a default" />
          </div>
        </Section>

        <Section icon={Car} title="Modelos" description="Autos nuevos" testid="car-builder-section-modelos">
          {sectionEditor('modelos')}
          {toggleRow('show_modelos', 'Mostrar sección Modelos')}
        </Section>

        <Section icon={CarFront} title="Seminuevos" description="Inventario usado" testid="car-builder-section-seminuevos">
          {sectionEditor('seminuevos')}
          {toggleRow('show_seminuevos', 'Mostrar sección Seminuevos')}
        </Section>

        <Section icon={Wrench} title="Refacciones" description="Refacciones y accesorios" testid="car-builder-section-refacciones">
          {sectionEditor('refacciones')}
          {toggleRow('show_refacciones', 'Mostrar sección Refacciones')}
        </Section>

        <Section icon={Calendar} title="Mi Auto" description="Agendar servicio / mantenimiento" testid="car-builder-section-mi-auto">
          {sectionEditor('mi_auto')}
          {toggleRow('show_mi_auto', 'Mostrar sección Mi Auto')}
        </Section>

        <Section icon={Megaphone} title="Promociones" description="Modal de ofertas por modelo" testid="car-builder-section-promotions">
          <FieldRow label="Título de la sección" counter={`${(promotions.heading || '').length}/60`}>
            <div className="flex gap-1">
              <Input type="text" maxLength={60} disabled={disabled} value={promotions.heading || ''} onChange={(e) => onPromotionsChange({ heading: e.target.value })} data-testid="car-builder-promotions-heading-input" />
              <RevertButton disabled={disabled || revertingField === 'promotions'} onClick={() => onRevert('promotions')} title="Restablecer Promociones a default" />
            </div>
          </FieldRow>
          <FieldRow label="Descripción" counter={`${(promotions.description || '').length}/200`}>
            <Textarea maxLength={200} disabled={disabled} className="min-h-16" value={promotions.description || ''} onChange={(e) => onPromotionsChange({ description: e.target.value })} data-testid="car-builder-promotions-description-input" />
          </FieldRow>
          <FieldRow label="Texto del botón" counter={`${(promotions.button_text || '').length}/40`}>
            <Input type="text" maxLength={40} disabled={disabled} value={promotions.button_text || ''} onChange={(e) => onPromotionsChange({ button_text: e.target.value })} data-testid="car-builder-promotions-button-input" />
          </FieldRow>
          {toggleRow('show_promociones', 'Mostrar sección Promociones')}
          <p className="text-[11px] text-gray-400">Sin items la sección se oculta sola en la landing, aunque el toggle esté activo — nunca un botón que abre un modal vacío.</p>

          <div className="pt-2 border-t border-gray-200 space-y-3">
            {promoItems.map((item, idx) => (
              <div key={item.id || idx} className="border border-gray-200 rounded-md p-2 space-y-1" data-testid={`promo-item-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-gray-600">Promoción {idx + 1}</span>
                  <Button type="button" variant="outline" size="icon" disabled={disabled} onClick={() => updatePromoItems(promoItems.filter((_, i) => i !== idx))} className="w-7 h-7 text-gray-500 hover:text-red-600" data-testid={`promo-item-delete-${idx}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
                <Input type="text" disabled={disabled} placeholder="Nombre del modelo" value={item.model_name || ''} onChange={(e) => updatePromoItem(idx, { model_name: e.target.value })} data-testid={`promo-item-model-${idx}`} />
                <div className="flex gap-1 items-center">
                  <Input type="text" disabled={disabled} placeholder="URL de imagen" value={item.image_url || ''} onChange={(e) => updatePromoItem(idx, { image_url: e.target.value })} data-testid={`promo-item-image-${idx}`} />
                  {item.image_url && <img src={item.image_url} alt="" className="w-9 h-9 object-cover rounded border border-gray-200 shrink-0" />}
                </div>
                <Input type="text" disabled={disabled} placeholder="Beneficio 1" value={item.benefit_1 || ''} onChange={(e) => updatePromoItem(idx, { benefit_1: e.target.value })} data-testid={`promo-item-benefit1-${idx}`} />
                <Input type="text" disabled={disabled} placeholder="Beneficio 2" value={item.benefit_2 || ''} onChange={(e) => updatePromoItem(idx, { benefit_2: e.target.value })} data-testid={`promo-item-benefit2-${idx}`} />
                <Input type="text" disabled={disabled} placeholder="Beneficio 3" value={item.benefit_3 || ''} onChange={(e) => updatePromoItem(idx, { benefit_3: e.target.value })} data-testid={`promo-item-benefit3-${idx}`} />
              </div>
            ))}
            <Button type="button" variant="link" size="sm" disabled={disabled} onClick={addPromoItem} className="h-auto p-0 text-xs" data-testid="promo-item-add-button"><Plus className="h-3.5 w-3.5" /> Agregar promoción</Button>
          </div>
        </Section>

        <Section icon={Phone} title="Contacto" description="Teléfono, dirección y horario" testid="car-builder-dealer-info-card">
          <FieldRow label="Teléfono">
            <Input type="text" disabled={disabled} value={dealerInfo.phone || ''} onChange={(e) => onDealerInfoChange({ phone: e.target.value })} data-testid="dealer-info-phone" />
            {!isLikelyPhone(dealerInfo.phone) && <p className={warnCls}>Revisa el formato (solo dígitos, espacios, +, - y paréntesis)</p>}
          </FieldRow>
          <FieldRow label="Dirección">
            <Input type="text" disabled={disabled} value={dealerInfo.address || ''} onChange={(e) => onDealerInfoChange({ address: e.target.value })} data-testid="dealer-info-address" />
          </FieldRow>
          <FieldRow label="Horarios">
            <Textarea disabled={disabled} className="min-h-20" placeholder="Lun-Vie 9:00-19:00, Sáb 9:00-14:00" value={dealerInfo.hours || ''} onChange={(e) => onDealerInfoChange({ hours: e.target.value })} data-testid="dealer-info-hours" />
          </FieldRow>
          <p className="text-[11px] text-gray-400">El correo de contacto se toma del perfil de tu empresa (Configuración), no se edita aquí.</p>
          <div className="pt-2 border-t border-gray-200">
            {toggleRow('show_cotizar', 'Habilitar botón "Cotizar" (nav + modal)')}
            {toggleRow('show_test_drive', 'Habilitar "Agendar prueba de manejo"')}
          </div>
          <div className="pt-1">
            <RevertButton disabled={disabled || revertingField === 'dealer_info'} onClick={() => onRevert('dealer_info')} title="Restablecer TODOS los datos del dealer (contacto, redes, mapa, WhatsApp)" />
          </div>
        </Section>

        <Section icon={Share2} title="Redes Sociales" description="Facebook, Instagram, TikTok" testid="car-builder-section-redes">
          <FieldRow label="URL Facebook">
            <Input type="text" disabled={disabled} value={dealerInfo.facebook_url || ''} onChange={(e) => onDealerInfoChange({ facebook_url: e.target.value })} data-testid="dealer-info-facebook" />
            {!isLikelyUrl(dealerInfo.facebook_url) && <p className={warnCls}>Debería empezar con http:// o https://</p>}
          </FieldRow>
          <FieldRow label="URL Instagram">
            <Input type="text" disabled={disabled} value={dealerInfo.instagram_url || ''} onChange={(e) => onDealerInfoChange({ instagram_url: e.target.value })} data-testid="dealer-info-instagram" />
            {!isLikelyUrl(dealerInfo.instagram_url) && <p className={warnCls}>Debería empezar con http:// o https://</p>}
          </FieldRow>
          <FieldRow label="URL TikTok">
            <Input type="text" disabled={disabled} value={dealerInfo.tiktok_url || ''} onChange={(e) => onDealerInfoChange({ tiktok_url: e.target.value })} data-testid="dealer-info-tiktok" />
            {!isLikelyUrl(dealerInfo.tiktok_url) && <p className={warnCls}>Debería empezar con http:// o https://</p>}
          </FieldRow>
          <p className="text-[11px] text-gray-400">Cada ícono se oculta solo en la landing si su URL está vacía. Se restablece junto con Contacto.</p>
        </Section>

        <Section icon={MapPin} title="Mapa" description="Se muestra solo si hay dirección o coordenadas" testid="car-builder-section-mapa">
          <div className="grid grid-cols-2 gap-2">
            <FieldRow label="Latitud">
              <Input
                type="text" disabled={disabled} value={dealerInfo.map_lat ?? ''}
                onChange={(e) => { const v = e.target.value; onDealerInfoChange({ map_lat: v === '' ? null : (isNaN(Number(v)) ? v : Number(v)) }); }}
                placeholder="19.4326" data-testid="dealer-info-lat"
              />
            </FieldRow>
            <FieldRow label="Longitud">
              <Input
                type="text" disabled={disabled} value={dealerInfo.map_lng ?? ''}
                onChange={(e) => { const v = e.target.value; onDealerInfoChange({ map_lng: v === '' ? null : (isNaN(Number(v)) ? v : Number(v)) }); }}
                placeholder="-99.1332" data-testid="dealer-info-lng"
              />
            </FieldRow>
          </div>
          <p className="text-[11px] text-gray-400">Sin toggle manual a propósito: sin lat/lng ni dirección, la sección se oculta sola — nunca un mapa roto.</p>
        </Section>

        <Section icon={MessageCircle} title="WhatsApp" description="Botón flotante en la landing" testid="car-builder-section-whatsapp">
          <FieldRow label="Número">
            <Input type="text" disabled={disabled} placeholder="+52 55 1234 5678" value={dealerInfo.whatsapp_number || ''} onChange={(e) => onDealerInfoChange({ whatsapp_number: e.target.value })} data-testid="dealer-info-whatsapp" />
            {!isLikelyPhone(dealerInfo.whatsapp_number) && <p className={warnCls}>Revisa el formato (incluye código de país)</p>}
          </FieldRow>
          <p className="text-[11px] text-gray-400">Sin toggle manual: el botón se oculta solo si el número está vacío.</p>
        </Section>

        <Section icon={Settings} title="Configuración de Página" description="Título de pestaña y favicon" testid="car-builder-section-meta">
          <FieldRow label="Título de la pestaña" counter={`${(site.page_title || '').length}/70`}>
            <div className="flex gap-1">
              <Input type="text" maxLength={70} disabled={disabled} placeholder={site.agency_name || 'Nombre de la agencia'} value={site.page_title || ''} onChange={(e) => onFieldChange({ page_title: e.target.value })} data-testid="car-builder-page-title-input" />
              <RevertButton disabled={disabled || revertingField === 'page_title'} onClick={() => onRevert('page_title')} title="Restablecer título de pestaña a default" />
            </div>
          </FieldRow>
          <FieldRow label="Favicon (ícono de la pestaña)">
            <div className="flex gap-1 items-center">
              <Input type="text" disabled={disabled} placeholder="https://..." value={site.favicon_url || ''} onChange={(e) => onFieldChange({ favicon_url: e.target.value })} data-testid="car-builder-favicon-input" />
              {site.favicon_url && <img src={site.favicon_url} alt="" className="w-6 h-6 object-contain shrink-0" />}
              <UploadButton disabled={disabled} uploading={uploadingField === 'favicon_url'} onFile={(file) => handleUpload('favicon_url', file)} title="Subir favicon" testid="car-builder-favicon-upload" />
              <RevertButton disabled={disabled || revertingField === 'favicon_url'} onClick={() => onRevert('favicon_url')} title="Restablecer favicon a default" />
            </div>
            {!isLikelyUrl(site.favicon_url) && <p className={warnCls}>Debería empezar con http:// o https://</p>}
          </FieldRow>
        </Section>

        <Section icon={Type} title="Tipografía" description="Fuente personalizada de toda la landing" testid="car-builder-section-fonts">
          {/* Simplificado a pedido de Alexis (2026-09-11), modelo dev-builder:
              solo subir UNA fuente custom. Los pickers de color/fondo de
              sección que vivían aquí se quitaron de la UI -- el backend
              (typography_colors) los sigue soportando como estaban, con sus
              defaults, por si algún tenant ya los tenía guardados; solo no
              hay control para editarlos desde el editor. */}
          <FieldRow label="Fuente personalizada (.ttf / .woff)">
            <div className="flex items-center gap-2">
              <UploadButton disabled={disabled} uploading={uploadingField === 'typography-font'} onFile={(file) => handleTypographyFontUpload(file)} title="Subir fuente (.ttf/.otf/.woff/.woff2)" testid="typography-font-upload" accept=".ttf,.otf,.woff,.woff2" />
              <span className="text-xs text-gray-600 truncate" data-testid="typography-font-filename">
                {typographyColors.font_file_url ? typographyColors.font_file_url.split('/').pop() : 'Ningún archivo — usa la fuente por default'}
              </span>
            </div>
          </FieldRow>
          <div className="pt-1">
            <RevertButton disabled={disabled || revertingField === 'typography_colors'} onClick={() => onRevert('typography_colors')} title="Quitar fuente personalizada" />
          </div>
        </Section>

        <Section icon={Palette} title="Colores por Sección" description="Fondo y color de texto de cada sección" testid="car-builder-section-colors-por-seccion">
          {[
            ['modelos', 'Modelos'],
            ['seminuevos', 'Seminuevos'],
            ['refacciones', 'Refacciones'],
            ['mi_auto', 'Mi Auto'],
            ['promociones', 'Promociones'],
          ].map(([key, label]) => (
            <div key={key} className="mb-3 pb-3 border-b border-gray-100 last:border-0 last:mb-0 last:pb-0">
              <p className="text-xs font-semibold text-gray-700 mb-2">{label}</p>
              <FieldRow>
                <ColorField
                  testid={`car-builder-section-bg-${key}`}
                  label="Fondo"
                  value={(typographyColors.section_background_colors || {})[key]}
                  defaultValue="#ffffff"
                  disabled={disabled}
                  onChange={(v) => updateSectionColor(key, 'bg', v)}
                />
              </FieldRow>
              <FieldRow>
                <ColorField
                  testid={`car-builder-section-text-${key}`}
                  label="Texto"
                  value={(typographyColors.section_text_colors || {})[key]}
                  defaultValue="#111827"
                  disabled={disabled}
                  onChange={(v) => updateSectionColor(key, 'text', v)}
                />
              </FieldRow>
            </div>
          ))}
          <p className="text-[11px] text-gray-400 mt-1">El color de texto aplica al título de la sección (y a la descripción, en Promociones).</p>
          <div className="pt-1">
            <RevertButton disabled={disabled || revertingField === 'typography_colors'} onClick={() => onRevert('typography_colors')} title="Restablecer TODA la Tipografía y Colores a default (fuente + colores)" />
          </div>
        </Section>

        {isAdmin && (
          <Section icon={KeyRound} title="Permisos" description="Quién más puede editar" testid="car-builder-section-permisos">
            <FieldRow label="IDs autorizados (uno por línea)">
              <Textarea className="min-h-20" value={(site.writer_user_ids || []).join('\n')} onChange={(e) => onWritersChange(e.target.value.split('\n').map((v) => v.trim()).filter(Boolean))} data-testid="car-builder-writers-textarea" />
            </FieldRow>
          </Section>
        )}
      </div>

      <div className="px-4 py-4 border-t border-gray-200 bg-gray-50 space-y-2" data-testid="car-builder-actions">
        {publishStatus === 'draft' && (
          <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 text-yellow-900 text-xs font-medium rounded" data-testid="car-builder-draft-banner">
            ⚠️ Aún no publicado — guarda y publica para que se vea en tu landing.
          </div>
        )}
        {publishStatus === 'pending' && (
          <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-900 text-xs font-medium rounded" data-testid="car-builder-pending-banner">
            💾 Guardado. Publica para que tus cambios se vean en vivo.
          </div>
        )}
        <Button
          type="button" onClick={onSave} disabled={saving || disabled}
          className="w-full bg-blue-600 hover:bg-blue-700"
          data-testid="car-builder-save-button"
        >
          {saving ? 'Guardando…' : '💾 Guardar Cambios'}
        </Button>
        <Button
          type="button" onClick={onPublish} disabled={publishing || disabled}
          className="w-full bg-gray-700 hover:bg-gray-800"
          data-testid="car-builder-publish-button"
        >
          {publishing ? 'Publicando…' : '📢 Publicar'}
        </Button>
        <Button
          type="button" variant="outline" onClick={onResetAll} disabled={resetting || disabled}
          className="w-full text-red-600 border-red-300 hover:bg-red-50"
          data-testid="car-builder-reset-all-button"
        >
          {resetting ? 'Restaurando…' : '↺ Restaurar a Defaults'}
        </Button>
      </div>
    </aside>
  );
}
